<template>
  <div class="catalog">
    <div class="catalog-search">
      <LgInput
        :model-value="search"
        placeholder="Search metrics…"
        aria-label="Search metrics"
        @update:model-value="$emit('update:search', $event)"
      >
        <template #lead><Search :size="14" aria-hidden="true" /></template>
      </LgInput>
    </div>

    <!-- SEAM (deferred, amendments §0): the "Changing right now" / movers section
         (auto-ranked anomalous metrics) lands here in v1.1, above the grouped
         catalog. Ship the grouped catalog + per-row sparkline now. -->

    <div v-if="loading" class="catalog-note">Loading metrics…</div>
    <div v-else-if="error" class="catalog-note err">Couldn't load metrics.</div>
    <div v-else-if="!groups.length" class="catalog-empty">
      <GoblinIcon variant="empty" :size="48" />
      <p class="catalog-empty-title">
        {{ search ? 'No metrics match that search.' : 'No metrics yet.' }}
      </p>
      <p v-if="search" class="catalog-empty-sub">Try a shorter or different term.</p>
    </div>

    <div v-else class="catalog-groups">
      <!-- Empty source groups are never emitted: `groups` is derived from the
           metrics themselves, so a source with no data simply doesn't appear
           (amendments §0 — hide empty Postgres/etc.). -->
      <section v-for="g in groups" :key="g.key" class="catalog-group">
        <button
          type="button"
          class="group-head"
          :aria-expanded="!collapsed[g.key]"
          @click="toggle(g.key)"
        >
          <ChevronDown
            class="group-caret"
            :class="{ closed: collapsed[g.key] }"
            :size="14"
            aria-hidden="true"
          />
          <span class="group-name">{{ g.label }}</span>
          <span class="group-count">{{ g.count }}</span>
        </button>

        <div v-show="!collapsed[g.key]">
          <template v-for="fam in g.families" :key="fam.key">
            <!-- Family subheading is only meaningful when a group has more than
                 one family; a lone family would just repeat the group. -->
            <div v-if="g.families.length > 1" class="family-head">{{ fam.label }}</div>
            <ul class="metric-list">
              <li v-for="m in fam.metrics" :key="metricKey(m)">
                <button
                  type="button"
                  class="metric-row"
                  :class="{ 'is-active': metricKey(m) === selectedKey }"
                  :title="`Add ${humanize(m.name)} to the chart`"
                  @click="$emit('pick', m)"
                >
                  <span class="metric-main">
                    <span class="metric-name">{{ humanize(m.name) }}</span>
                    <span v-if="m.description" class="metric-desc">{{ m.description }}</span>
                    <span class="metric-key">
                      {{ m.name }}
                      <template v-if="showTarget(g)"> · {{ m.target }}</template>
                      <template v-if="m.unit"> · {{ m.unit }}</template>
                    </span>
                  </span>

                  <span class="metric-preview">
                    <LgSparkline
                      v-if="m.sparkline && m.sparkline.length > 1"
                      class="metric-spark"
                      :points="m.sparkline"
                      tone="auto"
                      :width="60"
                      :height="20"
                    />
                    <span class="metric-value">{{ formatValue(m) }}</span>
                    <span v-if="hasDelta(m)" class="metric-delta" :class="`tone-${deltaTone(m)}`">
                      <component :is="deltaIcon(m)" :size="12" aria-hidden="true" />
                      {{ deltaText(m) }}
                    </span>
                  </span>
                </button>
              </li>
            </ul>
          </template>
        </div>
      </section>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, reactive } from 'vue';
import { Search, ChevronDown, ArrowUp, ArrowDown, Minus } from 'lucide-vue-next';
import type { CatalogMetric } from '../../api.js';
import { formatMetric, formatPercent } from '../../lib/format-metric.js';
import { unitFor, metricFamily } from '../../lib/metric-exploration.js';
import LgInput from '../../ui/LgInput.vue';
import LgSparkline from '../../ui/LgSparkline.vue';
import GoblinIcon from '../GoblinIcon.vue';

const props = defineProps<{
  metrics: CatalogMetric[];
  loading?: boolean;
  error?: boolean;
  search: string;
  /** `metricKey` of the metric currently on the chart, to mark it active. */
  selectedKey?: string | null;
}>();

defineEmits<{ pick: [metric: CatalogMetric]; 'update:search': [value: string] }>();

/** Stable identity for a catalog row: (source,target,name) per §2.1. */
function metricKey(m: CatalogMetric): string {
  return `${m.source} ${m.target} ${m.name}`;
}

const PROM_SUFFIXES = ['_bucket', '_count', '_sum', '_total'];
/** Turn a raw metric key into a readable title ("container.mem.bytes" →
 * "Container mem bytes"). The full sentence description sits below it. */
function humanize(name: string): string {
  let base = name;
  for (const s of PROM_SUFFIXES) {
    if (base.endsWith(s)) {
      base = base.slice(0, -s.length);
      break;
    }
  }
  const words = base.replace(/[._]+/g, ' ').trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

interface Family {
  key: string;
  label: string;
  order: number;
  metrics: CatalogMetric[];
}
interface Group {
  key: string;
  label: string;
  source: string;
  count: number;
  families: Family[];
}

/** Prometheus metrics split per target (api/traefik carry very different sets);
 * synthetic container/host metrics group by source with target shown per row. */
function groupLabel(m: CatalogMetric): { key: string; label: string } {
  if (m.source === 'prometheus') {
    return { key: `prometheus ${m.target}`, label: `prometheus · ${m.target}` };
  }
  return { key: m.source, label: m.source };
}

/** A prometheus group's label already names its target; container/host don't. */
function showTarget(g: Group): boolean {
  return g.source !== 'prometheus';
}

const filtered = computed<CatalogMetric[]>(() => {
  const q = props.search.trim().toLowerCase();
  if (!q) return props.metrics;
  const tokens = q.split(/\s+/);
  return props.metrics.filter((m) => {
    const hay = [
      m.name,
      m.description ?? '',
      m.help ?? '',
      m.source,
      m.target,
      humanize(m.name),
      ...m.labelKeys,
    ]
      .join(' ')
      .toLowerCase();
    return tokens.every((t) => hay.includes(t));
  });
});

interface GroupAcc {
  key: string;
  label: string;
  source: string;
  families: Map<string, Family>;
}

const groups = computed<Group[]>(() => {
  const map = new Map<string, GroupAcc>();
  for (const m of filtered.value) {
    const { key, label } = groupLabel(m);
    let g = map.get(key);
    if (!g) {
      g = { key, label, source: m.source, families: new Map() };
      map.set(key, g);
    }
    // Second level: bucket the metric into a family (CPU/Memory/Process/…) so a
    // long, similarly-named list stays legible (spec §4.2.1).
    const fam = metricFamily(m);
    let f = g.families.get(fam.key);
    if (!f) {
      f = { key: fam.key, label: fam.label, order: fam.order, metrics: [] };
      g.families.set(fam.key, f);
    }
    f.metrics.push(m);
  }
  const list = [...map.values()];
  // Friendly source order: prometheus groups first, then container, then host.
  const rank = (s: string) =>
    s === 'prometheus' ? 0 : s === 'container' ? 1 : s === 'host' ? 2 : 3;
  list.sort((a, b) => rank(a.source) - rank(b.source) || a.label.localeCompare(b.label));
  return list.map((g) => {
    const families = [...g.families.values()].sort(
      (a, b) => a.order - b.order || a.label.localeCompare(b.label),
    );
    for (const f of families) f.metrics.sort((a, b) => a.name.localeCompare(b.name));
    const count = families.reduce((n, f) => n + f.metrics.length, 0);
    return { key: g.key, label: g.label, source: g.source, count, families };
  });
});

const collapsed = reactive<Record<string, boolean>>({});
function toggle(key: string) {
  collapsed[key] = !collapsed[key];
}

// --- Per-row value + delta ------------------------------------------------
function formatValue(m: CatalogMetric): string {
  if (m.lastValue == null) return '—';
  return formatMetric(m.lastValue, unitFor(m));
}
function hasDelta(m: CatalogMetric): boolean {
  return m.deltaPct != null && Number.isFinite(m.deltaPct);
}
function direction(m: CatalogMetric): 'up' | 'down' | 'flat' {
  const d = m.deltaPct ?? 0;
  return d > 0 ? 'up' : d < 0 ? 'down' : 'flat';
}
function deltaIcon(m: CatalogMetric) {
  const dir = direction(m);
  return dir === 'up' ? ArrowUp : dir === 'down' ? ArrowDown : Minus;
}
function deltaText(m: CatalogMetric): string {
  if (direction(m) === 'flat') return 'flat';
  return formatPercent(Math.abs(m.deltaPct ?? 0));
}
/** Spec convention: rising = warn, falling = ok, flat = neutral. */
function deltaTone(m: CatalogMetric): 'up' | 'down' | 'flat' {
  return direction(m);
}
</script>

<style scoped>
.catalog {
  display: flex;
  flex-direction: column;
  min-height: 0;
  height: 100%;
}
.catalog-search {
  flex-shrink: 0;
  padding: var(--lg-space-2);
  border-bottom: 1px solid var(--lg-border-faint);
}
.catalog-note {
  padding: var(--lg-space-3);
  color: var(--lg-text-faint);
  font-size: var(--lg-fs-sm);
}
.catalog-note.err {
  color: var(--lg-danger);
}
.catalog-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--lg-space-2);
  padding: var(--lg-space-6) var(--lg-space-3);
  text-align: center;
  color: var(--lg-text-faint);
}
.catalog-empty-title {
  margin: 0;
  color: var(--lg-text-dim);
}
.catalog-empty-sub {
  margin: 0;
  font-size: var(--lg-fs-sm);
}

.catalog-groups {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
}
.catalog-group {
  border-bottom: 1px solid var(--lg-border-faint);
}
.group-head {
  display: flex;
  align-items: center;
  gap: var(--lg-space-2);
  width: 100%;
  min-height: var(--lg-control-md);
  padding: 0 var(--lg-space-3);
  border: none;
  background: var(--lg-bg-surface);
  color: var(--lg-text);
  font: inherit;
  font-family: var(--lg-font-mono);
  font-size: var(--lg-fs-sm);
  cursor: pointer;
  position: sticky;
  top: 0;
  z-index: 1;
}
.group-head:hover {
  background: var(--lg-bg-hover);
}
.group-caret {
  flex-shrink: 0;
  color: var(--lg-text-dim);
  transition: transform var(--lg-dur-fast) var(--lg-ease);
}
.group-caret.closed {
  transform: rotate(-90deg);
}
.group-name {
  flex: 1;
  text-align: left;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.group-count {
  flex-shrink: 0;
  color: var(--lg-text-faint);
  font-size: var(--lg-fs-xs);
}

.family-head {
  padding: var(--lg-space-2) var(--lg-space-3) 2px;
  color: var(--lg-text-dim);
  font-size: var(--lg-fs-xs);
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}
.metric-list {
  margin: 0;
  padding: 0;
  list-style: none;
}
.metric-row {
  display: flex;
  align-items: center;
  gap: var(--lg-space-3);
  width: 100%;
  padding: var(--lg-space-2) var(--lg-space-3);
  border: none;
  border-left: 3px solid transparent;
  background: transparent;
  color: var(--lg-text);
  font: inherit;
  text-align: left;
  cursor: pointer;
}
.metric-row:hover {
  background: var(--lg-bg-hover);
}
.metric-row.is-active {
  border-left-color: var(--lg-accent);
  background: var(--lg-bg-active);
}
.metric-main {
  display: flex;
  flex-direction: column;
  gap: 1px;
  min-width: 0;
  flex: 1;
}
.metric-name {
  font-size: var(--lg-fs-sm);
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.metric-desc {
  font-size: var(--lg-fs-xs);
  color: var(--lg-text-dim);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.metric-key {
  font-family: var(--lg-font-mono);
  font-size: var(--lg-fs-xs);
  color: var(--lg-text-faint);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.metric-preview {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 2px;
  flex-shrink: 0;
}
.metric-spark {
  opacity: 0.9;
}
.metric-value {
  font-family: var(--lg-font-mono);
  font-size: var(--lg-fs-sm);
  color: var(--lg-text);
}
.metric-delta {
  display: inline-flex;
  align-items: center;
  gap: 1px;
  font-size: var(--lg-fs-xs);
  font-weight: 600;
}
.metric-delta.tone-up {
  color: var(--lg-warn);
}
.metric-delta.tone-down {
  color: var(--lg-ok);
}
.metric-delta.tone-flat {
  color: var(--lg-text-faint);
}
</style>
